/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  ExternalLink,
  Funnel,
  Link2,
  Pencil,
  Users,
} from "lucide-react";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { MunicipalityData } from "../../../models/MunicipalityData";
import { OrganizationData } from "../../../models/OrganizationData";
import ConfigurableTableLayout from "../../Common/ConfigurableTableLayout";
import { DemoBadge } from "../../Common/DemoBadge";
import OverflowBadge from "../../Common/OverflowBadge";
import TableRowActions, {
  TableRowActionItem,
} from "../../Common/TableRowActions";
import ColumnTextFilterPopover from "../../FacilityListComponents/RefactoredFacilityTable/ColumnTextFilterPopover";
import { ListTopBar } from "../ListTopBar";

import "./styles.scss";

export interface IOrganizationManagementListProps {
  dataList: OrganizationData[];
  totalCount: number;
  filterFormData: any;
  organizationId: string;
  onChangeFilterFormData: (filterFormData: any) => void;
  onClickEdit: (organizationItem: OrganizationData) => void;
  onClickLinkLocations: (organizationItem: OrganizationData) => void;
}

type OrganizationColumnId =
  | "name"
  | "creation_date"
  | "expiration_date"
  | "municipalities"
  | "number_of_facilities"
  | "number_of_users"
  | "actions";

type OrganizationSortField =
  | "name"
  | "plan_expiry_date"
  | "type"
  | "health_index"
  | "login_rate"
  | "sign_up_rate";

type SortOrder = "asc" | "desc";

const ALL_COLUMNS: OrganizationColumnId[] = [
  "name",
  "creation_date",
  "expiration_date",
  "municipalities",
  "number_of_facilities",
  "number_of_users",
  "actions",
];

const DEFAULT_VISIBLE_COLUMNS: OrganizationColumnId[] = [...ALL_COLUMNS];
const VISIBLE_COLUMNS_STORAGE_KEY =
  "OrganizationManagementPage_RefactoredVisibleColumns";

const SORT_LABELS: Record<
  OrganizationSortField,
  { asc: string; desc: string }
> = {
  name: { asc: "団体名 ↑", desc: "団体名 ↓" },
  type: { asc: "種別 ↑", desc: "種別 ↓" },
  plan_expiry_date: { asc: "利用期限 ↑", desc: "利用期限 ↓" },
  health_index: { asc: "施設の'A'健康施設率 ↑", desc: "施設の'A'健康施設率 ↓" },
  login_rate: { asc: "施設のログイン率 ↑", desc: "施設のログイン率 ↓" },
  sign_up_rate: { asc: "サインアップ率 ↑", desc: "サインアップ率 ↓" },
};

const COLUMN_SORT_FIELD: Partial<
  Record<OrganizationColumnId, OrganizationSortField>
> = {
  name: "name",
  expiration_date: "plan_expiry_date",
};

const normalizeVisibleColumns = (value: unknown): OrganizationColumnId[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const filtered = value.filter((col): col is OrganizationColumnId =>
    ALL_COLUMNS.includes(col as OrganizationColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const required = new Set<OrganizationColumnId>(["name", "actions"]);
  unique.forEach((item) => required.add(item));

  return ALL_COLUMNS.filter((item) => required.has(item));
};

export const OrganizationManagementList: React.FunctionComponent<
  IOrganizationManagementListProps
> = (props) => {
  const { t: _t } = useTranslation();
  const t = useCallback(
    (key: string) =>
      _t(`organizationManagementPage.organizationManagementList.${key}`),
    [_t]
  );

  const {
    dataList,
    totalCount,
    filterFormData, //organizationId,
    onChangeFilterFormData,
    onClickEdit,
    onClickLinkLocations,
  } = props;

  const [visibleColumns, setVisibleColumns] = useState<OrganizationColumnId[]>(
    () => {
      const savedColumns = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
      if (!savedColumns) {
        return DEFAULT_VISIBLE_COLUMNS;
      }

      try {
        return normalizeVisibleColumns(JSON.parse(savedColumns));
      } catch {
        return DEFAULT_VISIBLE_COLUMNS;
      }
    }
  );
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [activeFilterColumn, setActiveFilterColumn] = useState<string | null>(
    null
  );
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);

  const currentSortField =
    (filterFormData.sort as OrganizationSortField) || "plan_expiry_date";
  const currentSortOrder: SortOrder =
    filterFormData.order === "desc" ? "desc" : "asc";

  const activeConditionChips = useMemo(() => {
    const sortFieldLabel = (() => {
      switch (currentSortField) {
        case "name":
          return t("organization_name");
        case "plan_expiry_date":
          return t("expiration_date");
        case "type":
          return "種別";
        case "health_index":
          return "施設の'A'健康施設率";
        case "login_rate":
          return "施設のログイン率";
        case "sign_up_rate":
          return "サインアップ率";
        default:
          return t("organization_name");
      }
    })();

    const chips: { id: string; label: string; onRemove: () => void }[] = [
      {
        id: `sort-${currentSortField}`,
        label: `並び替え順: ${sortFieldLabel} ${
          currentSortOrder === "desc" ? "降順" : "昇順"
        }`,
        onRemove: () => {
          onChangeFilterFormData({
            ...filterFormData,
            sort: "plan_expiry_date",
            order: "asc",
            sortBy: SORT_LABELS.plan_expiry_date.asc,
            pageIndex: 1,
          });
        },
      },
    ];

    const hasNameFilter = (filterFormData.searchName || "").trim().length > 0;
    if (hasNameFilter) {
      chips.push({
        id: "name-filter",
        label: `${t("organization_name")}: ${filterFormData.searchName}`,
        onRemove: () => {
          onChangeFilterFormData({
            ...filterFormData,
            searchName: "",
            pageIndex: 1,
          });
        },
      });
    }

    return chips;
  }, [
    currentSortField,
    currentSortOrder,
    filterFormData,
    onChangeFilterFormData,
    t,
  ]);

  const applySort = (field: OrganizationSortField, order: SortOrder) => {
    onChangeFilterFormData({
      ...filterFormData,
      sort: field,
      order,
      sortBy: SORT_LABELS[field][order],
      pageIndex: 1,
    });
  };

  const onSelectSortField = (columnId: OrganizationColumnId) => {
    const field = COLUMN_SORT_FIELD[columnId];
    if (!field) {
      return;
    }

    const nextOrder: SortOrder =
      currentSortField === field && currentSortOrder === "asc" ? "desc" : "asc";

    applySort(field, nextOrder);
  };

  const toggleColumnVisibility = (columnId: OrganizationColumnId) => {
    if (columnId === "name" || columnId === "actions") {
      return;
    }

    setVisibleColumns((prev) => {
      const next = prev.includes(columnId)
        ? normalizeVisibleColumns(prev.filter((col) => col !== columnId))
        : normalizeVisibleColumns([...prev, columnId]);

      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const getDynamicGridTemplateStyle = () => {
    // Fixed widths so columns never resize as pagination swaps in rows with
    // different content lengths (a `minmax(max-content, ...)` track is
    // recalculated from whatever rows are currently mounted, so it would
    // change size on every page). Content wider than the column truncates
    // with "..." + a title tooltip; the table scrolls horizontally instead
    // of squishing columns. `name` gets a fixed floor plus `1fr` so it
    // absorbs any leftover container width instead of leaving the table
    // looking cut short when there aren't enough columns to fill the row
    // (the floor is a constant, not content-derived, so this still can't
    // resize between pages).
    const colWidths: Record<OrganizationColumnId, string> = {
      name: "minmax(260px, 1fr)",
      creation_date: "170px",
      expiration_date: "220px",
      municipalities: "200px",
      number_of_facilities: "150px",
      number_of_users: "180px",
      actions: "96px",
    };

    return {
      gridTemplateColumns: visibleColumns
        .map((col) => colWidths[col])
        .join(" "),
    };
  };

  const getColumnLabelText = (columnId: OrganizationColumnId) => {
    switch (columnId) {
      case "name":
        return t("organization_name");
      case "creation_date":
        return t("creation_date");
      case "expiration_date":
        return t("expiration_date");
      case "municipalities":
        return t("municipalities");
      case "number_of_facilities":
        return t("number_of_facilities");
      case "number_of_users":
        return t("number_of_users");
      case "actions":
        return "操作";
    }
  };

  const onApplyHeaderFilter = (columnId: string, value: string) => {
    if (columnId === "name") {
      onChangeFilterFormData({
        ...filterFormData,
        searchName: value,
        pageIndex: 1,
      });
    }
  };

  const onClearHeaderFilter = (columnId: string) => {
    if (columnId === "name") {
      onChangeFilterFormData({
        ...filterFormData,
        searchName: "",
        pageIndex: 1,
      });
    }
  };

  const createSortButton = (
    columnId: OrganizationColumnId,
    labelText: string
  ) => {
    const sortField = COLUMN_SORT_FIELD[columnId];
    if (!sortField) {
      return <span className="column-label-text">{labelText}</span>;
    }

    const isActive = currentSortField === sortField;

    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => onSelectSortField(columnId)}
      >
        <span>{labelText}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            currentSortOrder === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  const renderFilterTrigger = (columnId: OrganizationColumnId) => {
    if (columnId !== "name") {
      return null;
    }

    const hasActiveFilter = (filterFormData.searchName || "").trim().length > 0;

    return (
      <div className="filter-trigger-wrap">
        <button
          type="button"
          className={`filter-trigger-btn ${
            hasActiveFilter ? "has-filter" : ""
          }`}
          onClick={(event) => {
            event.stopPropagation();
            setActiveFilterColumn((prev) =>
              prev === columnId ? null : columnId
            );
          }}
          aria-label={`filter-${columnId}`}
        >
          <Funnel className="funnel-icon" size={14} strokeWidth={2} />
        </button>
        {activeFilterColumn === columnId && (
          <ColumnTextFilterPopover
            columnId={columnId}
            title={getColumnLabelText(columnId)}
            value={filterFormData.searchName || ""}
            onApply={(value) => onApplyHeaderFilter(columnId, value)}
            onClear={() => onClearHeaderFilter(columnId)}
            onClose={() => setActiveFilterColumn(null)}
          />
        )}
      </div>
    );
  };

  // get Expiration Date Label
  const getExpirationDateLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();

    const usedTime = date.valueOf() - today.valueOf();
    const daysLeft = Math.floor(usedTime / (24 * 3600 * 1000));

    const label = `${date.getFullYear()}/${
      date.getMonth() + 1 < 10 ? "0" : ""
    }${date.getMonth() + 1}/${date.getDate() < 10 ? "0" : ""}${date.getDate()}`;

    return {
      daysLeft,
      label,
    };
  };

  const renderMunicipalities = (municipalities: MunicipalityData[]) => {
    if (municipalities.length === 0) {
      return "-";
    }

    const remainingMunicipalities = municipalities.slice(1);

    return (
      <div className="municipality-summary">
        <span className="cell-ellipsis">{municipalities[0].name}</span>
        <OverflowBadge
          count={remainingMunicipalities.length}
          tooltip={remainingMunicipalities
            .map((municipality) => municipality.name)
            .join(", ")}
        />
      </div>
    );
  };

  return (
    <>
      <div className="white-border-panel">
        <ListTopBar
          filterFormData={filterFormData}
          onChangeFilterFormData={(filterFormData: any) => {
            onChangeFilterFormData(filterFormData);
          }}
        />

        <ConfigurableTableLayout
          className="organization-management-table-layout"
          onContainerClick={() => {
            setShowColumnSettings(false);
            setOpenActionMenuId(null);
          }}
          controls={
            <div
              className="column-settings-container"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                className="btn-column-settings"
                onClick={() => setShowColumnSettings((prev) => !prev)}
              >
                表示項目の設定 ⚙️
              </button>
              {showColumnSettings && (
                <div className="column-settings-dropdown">
                  <div className="dropdown-title">表示列の切り替え</div>
                  {ALL_COLUMNS.map((colId) => (
                    <label key={`setting-${colId}`} className="setting-label">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(colId)}
                        disabled={colId === "name" || colId === "actions"}
                        onChange={() => toggleColumnVisibility(colId)}
                      />
                      <span className="checkbox-custom"></span>
                      <span className="label-text">
                        {getColumnLabelText(colId)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          }
          conditionChips={activeConditionChips}
          onClearAllConditions={() => {
            onChangeFilterFormData({
              ...filterFormData,
              searchName: "",
              sort: "plan_expiry_date",
              order: "asc",
              sortBy: SORT_LABELS.plan_expiry_date.asc,
              pageIndex: 1,
            });
          }}
          clearAllLabel={_t(
            "organizationManagementPage.organizationManagementList.listTopBar.clear_condition"
          )}
          tableContent={
            <div className="top-scrollbar">
              <div
                className="table-data table-data-grid scrollable"
                style={getDynamicGridTemplateStyle()}
              >
                <div className="row-th refactored-facility-list-header">
                  {visibleColumns.map((columnId) => (
                    <div
                      className={`col-th column-${columnId} ${
                        columnId === "actions" ? "text-right" : "center"
                      }`}
                      key={`header-${columnId}`}
                    >
                      <div
                        className={`spacing header-cell-inner ${
                          columnId === "name" ? "is-name" : ""
                        }`}
                      >
                        {createSortButton(
                          columnId,
                          getColumnLabelText(columnId)
                        )}
                        {renderFilterTrigger(columnId)}
                      </div>
                    </div>
                  ))}
                </div>
                {dataList
                  .slice(0, filterFormData.numberPerPage)
                  .map((item: OrganizationData, indexFacility: number) => (
                    <div className="row-td" key={indexFacility}>
                      {visibleColumns.includes("name") && (
                        <div className="col-td organization-name-column">
                          <div className="spacing">
                            {item.is_demo && <DemoBadge />}
                            <NavLink
                              to={`/dashboard/organization/${item.id}`}
                              className="blue-txt organization-name-link"
                              title={item.name}
                            >
                              <span className="cell-ellipsis">{item.name}</span>
                            </NavLink>
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("creation_date") && (
                        <div className="col-td creation-date-column">
                          <div className="spacing">
                            <span className="blue-txt">
                              {!item.created_at
                                ? `-`
                                : _t(
                                    "organizationManagementPage.organizationManagementList.creationdate",
                                    {
                                      date: moment(item.created_at).format(
                                        t("dateformat")
                                      ),
                                    }
                                  )}
                            </span>
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("expiration_date") && (
                        <div className="col-td expiration-date-column">
                          <div className="spacing">
                            <div
                              className={`txt ${
                                getExpirationDateLabel(item.plan_expiry_date)
                                  .daysLeft < 60
                                  ? "red-txt"
                                  : ""
                              }`}
                            >
                              {
                                getExpirationDateLabel(item.plan_expiry_date)
                                  .label
                              }
                              {" ("}
                              {getExpirationDateLabel(item.plan_expiry_date)
                                .daysLeft >= 0 ? (
                                <>
                                  {_t(
                                    "organizationManagementPage.organizationManagementList.days_left",
                                    {
                                      daysLeft: getExpirationDateLabel(
                                        item.plan_expiry_date
                                      ).daysLeft,
                                    }
                                  )}
                                </>
                              ) : (
                                <>{t("expired")}</>
                              )}
                              {")"}
                            </div>
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("municipalities") && (
                        <div className="col-td municipalities-column">
                          <div className="spacing">
                            {renderMunicipalities(item.municipalities)}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("number_of_facilities") && (
                        <div className="col-td numeric-column">
                          <div className="spacing">
                            {item?.locations?.length}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("number_of_users") && (
                        <div className="col-td numeric-column">
                          <div className="spacing">
                            {item?.accounts?.length}
                          </div>
                        </div>
                      )}
                      {visibleColumns.includes("actions") && (
                        <div className="col-td center buttons-section">
                          <TableRowActions
                            actions={
                              [
                                {
                                  key: "see_screens",
                                  label: t("see_screens"),
                                  icon: ExternalLink,
                                  href: `/dashboard/organization/${item.id}`,
                                },
                                {
                                  key: "edit",
                                  label: t("edit"),
                                  icon: Pencil,
                                  onClick: () => onClickEdit(item),
                                },
                                {
                                  key: "edit_members",
                                  label: t("edit_members"),
                                  icon: Users,
                                  href: `/admin/organizations/${item.id}/accounts`,
                                },
                                {
                                  key: "link_locations",
                                  label: t("link_locations"),
                                  icon: Link2,
                                  onClick: () => onClickLinkLocations(item),
                                },
                              ] as TableRowActionItem[]
                            }
                            isMenuOpen={openActionMenuId === item.id}
                            onToggleMenu={() =>
                              setOpenActionMenuId((currentId) =>
                                currentId === item.id ? null : item.id
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          }
          pagination={{
            filterFormData,
            totalRowsNumber: totalCount,
            onChangeFilterFormData: (nextFilterFormData: any) => {
              onChangeFilterFormData(nextFilterFormData);
            },
          }}
        />
      </div>
    </>
  );
};

export default OrganizationManagementList;
